import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowForward,
  Close,
  KeyboardReturn,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import NavigationIcon from "./NavigationIcon";
import {
  getNavigation,
  matchesNavigationSearch,
  NavigationIcon as IconName,
} from "../utils/navigation";

type Result = {
  label: string;
  description: string;
  path: string;
  icon: IconName;
  category: string;
};
export default function GlobalSearch() {
  const { state } = useApp();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const role = state.auth.user?.role;
  const pages = useMemo(() => getNavigation(role), [role]);
  const results = useMemo((): Result[] => {
    const pageResults = pages
      .filter((p) =>
        !query.trim()
          ? p.menu !== false &&
            [
              "/dashboard",
              "/machines",
              "/licenses",
              "/reports",
              "/worklists",
              "/messages",
            ].includes(p.path)
          : matchesNavigationSearch(query, [
              p.label,
              p.description,
              p.keywords || "",
            ]),
      )
      .sort((a, b) => Number(matchesNavigationSearch(query, [b.label])) - Number(matchesNavigationSearch(query, [a.label])))
      .slice(0, 8)
      .map((p) => ({
        ...p,
        category: query.trim() ? "Pagine e strumenti" : "Accesso rapido",
      }));
    if (!query.trim()) return pageResults;
    const machines: Result[] = state.machines
      .filter((m) =>
        matchesNavigationSearch(query, [
          m.name,
          m.serialNumber,
          m.model,
          m.companyName || "",
          m.location || "",
        ]),
      )
      .slice(0, 5)
      .map((m) => ({
        label: m.name,
        description: `${m.serialNumber} · ${m.companyName || "Cliente non assegnato"}`,
        path: `/machines/${m.id}`,
        icon: "machines",
        category: "Macchine",
      }));
    return [...machines, ...pageResults];
  }, [pages, query, state.machines]);
  const close = () => {
    setOpen(false);
    setQuery("");
    setSelected(0);
  };
  const go = (result: Result) => {
    close();
    navigate(result.path);
  };
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
        setQuery("");
        setSelected(0);
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);
  useEffect(() => {
    if (open)
      document
        .getElementById(`global-result-${selected}`)
        ?.scrollIntoView?.({ block: "nearest" });
  }, [selected, open]);
  const activeIndex = Math.min(selected, Math.max(0, results.length - 1));
  return (
    <>
      <Button
        color="inherit"
        onClick={() => setOpen(true)}
        aria-label="Cerca pagine e macchine"
        startIcon={<Search fontSize="small" />}
        sx={{
          justifyContent: "flex-start",
          color: "text.secondary",
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
          minWidth: { xs: 38, lg: 250 },
          px: { xs: 1, lg: 1.5 },
          "& .MuiButton-startIcon": { mr: { xs: 0, lg: 1 } },
        }}
      >
        <Box
          component="span"
          sx={{
            display: { xs: "none", lg: "block" },
            flexGrow: 1,
            textAlign: "left",
          }}
        >
          Cerca nella piattaforma
        </Box>
        <Box
          component="kbd"
          sx={{
            display: { xs: "none", lg: "block" },
            ml: 3,
            fontSize: 10,
            fontFamily: "inherit",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            px: 0.6,
            py: 0.2,
          }}
        >
          Ctrl K
        </Box>
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="sm"
        fullScreen={mobile}
        aria-labelledby="global-search-title"
        slotProps={{
          paper: {
            sx: {
              alignSelf: mobile ? undefined : "flex-start",
              mt: mobile ? 0 : "12vh",
              borderRadius: mobile ? 0 : 3,
              overflow: "hidden",
            },
          },
        }}
      >
        <Typography
          id="global-search-title"
          component="h2"
          sx={{
            px: 3,
            pt: 2,
            pb: 0.5,
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Navigazione rapida
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 2.5, py: 1.5 }}
        >
          <Search sx={{ color: "primary.main" }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Cerca una pagina, un cliente o una matricola…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            inputProps={{
              role: "combobox",
              "aria-label": "Cerca nella piattaforma",
              "aria-expanded": true,
              "aria-controls": "global-search-results",
              "aria-autocomplete": "list",
              "aria-activedescendant": results.length
                ? `global-result-${activeIndex}`
                : undefined,
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" && results.length) {
                event.preventDefault();
                setSelected((activeIndex + 1) % results.length);
              }
              if (event.key === "ArrowUp" && results.length) {
                event.preventDefault();
                setSelected(
                  (activeIndex - 1 + results.length) % results.length,
                );
              }
              if (event.key === "Enter" && results[activeIndex]) {
                event.preventDefault();
                go(results[activeIndex]);
              }
              if (event.key === "Escape") close();
            }}
            sx={{ fontSize: "1rem" }}
          />
          <IconButton aria-label="Chiudi ricerca" onClick={close} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <DialogContent
          sx={{ p: 1, minHeight: 160, maxHeight: mobile ? undefined : 410 }}
        >
          <List
            role="listbox"
            id="global-search-results"
            aria-label="Risultati di ricerca"
            disablePadding
          >
            {results.map((result, index) => (
              <React.Fragment key={result.path}>
                {(index === 0 ||
                  results[index - 1].category !== result.category) && (
                  <Typography
                    component="li"
                    role="presentation"
                    variant="overline"
                    sx={{
                      display: "block",
                      px: 1.5,
                      pt: 1,
                      pb: 0.5,
                      color: "text.secondary",
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {result.category}
                  </Typography>
                )}
                <ListItemButton
                  role="option"
                  id={`global-result-${index}`}
                  aria-selected={activeIndex === index}
                  selected={activeIndex === index}
                  tabIndex={-1}
                  onMouseMove={() => setSelected(index)}
                  onClick={() => go(result)}
                  sx={{ borderRadius: 1.5, py: 1.2, px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                    <NavigationIcon name={result.icon} />
                  </ListItemIcon>
                  <ListItemText
                    primary={result.label}
                    secondary={result.description}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
                  />
                  <ArrowForward
                    sx={{ color: "text.disabled", fontSize: 17, ml: 1 }}
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
          {!results.length && (
            <Box role="status" sx={{ textAlign: "center", px: 3, py: 5 }}>
              <Search sx={{ color: "text.disabled", fontSize: 32, mb: 1 }} />
              <Typography fontWeight={600}>
                Nessun risultato per “{query}”
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Prova il nome di un software, una pagina o la matricola di una
                macchina.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.4, bgcolor: "action.hover" }}
        >
          <Typography variant="caption" color="text.secondary">
            ↑ ↓ per scegliere · Invio per aprire
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <KeyboardReturn sx={{ fontSize: 13 }} /> Esc per chiudere
          </Typography>
        </Stack>
      </Dialog>
    </>
  );
}
